import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { MoreHorizontal, LogOut, RefreshCw, Volume2, VolumeX, X } from 'lucide-react'

import audioManager from '../../services/audioManager'
import { NAV_GROUPS, MOBILE_TABS, getUserItems, PINNED_ITEM, isPathActive, resolveBadge, visibleNavGroups } from './navConfig'

// Mobile bottom tab bar (primary destinations) + "More" bottom sheet
// for the remaining destinations and account utilities.

const TabItem = ({ tab, active, badge }) => (
    <Link
        to={tab.path}
        aria-current={active ? 'page' : undefined}
        className="sidebar-nav-link relative flex flex-1 flex-col items-center gap-0.5 py-1.5 text-[11px] font-medium transition-colors"
    >
        <span className="relative">
            <tab.icon size={22} className={active ? 'text-accent-blue' : 'text-text-muted'} />
            {badge && (
                <span className={`absolute -right-1.5 -top-1 rounded-full bg-accent-red ${badge.count > 0 ? 'min-w-[14px] h-[14px] px-0.5 flex items-center justify-center text-[9px] font-bold text-white' : 'h-2 w-2 ring-2 ring-bg-darkest'}`}>
                    {badge.count > 0 ? (badge.count > 9 ? '9+' : badge.count) : ''}
                </span>
            )}
        </span>
        <span className={active ? 'text-accent-blue' : 'text-text-muted'}>{tab.label}</span>
        {active && <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-accent-blue" />}
    </Link>
)

const SheetRow = ({ item, active, badge, onClick }) => (
    <Link
        to={item.path}
        onClick={onClick}
        className={`sidebar-nav-link flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors ${
            active ? 'bg-bg-hover text-text-lighter' : 'text-text-muted hover:bg-bg-hover/50 hover:text-text-light'
        }`}
    >
        <item.icon size={18} className={`flex-shrink-0 ${active ? 'text-accent-blue' : ''}`} />
        <span className="truncate">{item.label}</span>
        {badge && (
            <span className="ml-auto flex-shrink-0 h-2 w-2 rounded-full bg-accent-red" />
        )}
    </Link>
)

const MobileNav = ({ currentUser, demoModeEnabled, onResetCharacter, onLogout, playerMeta }) => {
    const location = useLocation()
    const { badges } = playerMeta
    const [sheetOpen, setSheetOpen] = useState(false)
    const [soundEnabled, setSoundEnabled] = useState(audioManager.isEnabled())

    const closeSheet = () => setSheetOpen(false)
    const userItems = getUserItems({ demoModeEnabled, handle: currentUser?.handle })
    const navGroups = visibleNavGroups(NAV_GROUPS, currentUser?.is_admin)

    const handleSoundToggle = () => setSoundEnabled(audioManager.toggle())

    return (
        <>
            {/* Bottom tab bar */}
            <nav className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border-light bg-bg-darkest/95 backdrop-blur-sm pb-[env(safe-area-inset-bottom)]">
                <div className="flex items-stretch">
                    {MOBILE_TABS.map(tab => (
                        <TabItem
                            key={tab.path}
                            tab={tab}
                            active={isPathActive(tab.path, location.pathname)}
                            badge={resolveBadge(tab, badges)}
                        />
                    ))}
                    <button
                        onClick={() => setSheetOpen(true)}
                        className="relative flex flex-1 flex-col items-center gap-0.5 py-1.5 text-[11px] font-medium text-text-muted transition-colors"
                        aria-label="More navigation"
                    >
                        <span className="relative">
                            <MoreHorizontal size={22} />
                            {(badges.achievements > 0 || badges.market || badges.daily) && (
                                <span className="absolute -right-1.5 -top-1 h-2 w-2 rounded-full bg-accent-red ring-2 ring-bg-darkest" />
                            )}
                        </span>
                        <span>More</span>
                    </button>
                </div>
            </nav>

            {/* More sheet */}
            <AnimatePresence>
                {sheetOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="lg:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                            onClick={closeSheet}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                            className="lg:hidden fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-y-auto rounded-t-2xl border-t border-border-light bg-bg-darkest shadow-2xl pb-[env(safe-area-inset-bottom)] custom-scrollbar"
                        >
                            <div className="sticky top-0 flex items-center justify-between border-b border-border-light bg-bg-darkest px-4 py-3">
                                <span className="text-sm font-bold uppercase tracking-wider text-text-light">More</span>
                                <button onClick={closeSheet} className="p-1 text-text-muted hover:text-white" aria-label="Close menu">
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="space-y-4 px-2 py-3">
                                <div className="flex flex-col gap-0.5">
                                    <SheetRow item={PINNED_ITEM} active={isPathActive(PINNED_ITEM.path, location.pathname)} onClick={closeSheet} />
                                </div>
                                {navGroups.map(group => (
                                    <div key={group.key}>
                                        <div className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                                            {group.label}
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            {group.items.map(item => (
                                                <SheetRow
                                                    key={item.path}
                                                    item={item}
                                                    active={isPathActive(item.path, location.pathname)}
                                                    badge={resolveBadge(item, badges)}
                                                    onClick={closeSheet}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                <div>
                                    <div className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                                        Account
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        {userItems.map(item => (
                                            <SheetRow
                                                key={item.path}
                                                item={item}
                                                active={isPathActive(item.path, location.pathname)}
                                                badge={resolveBadge(item, badges)}
                                                onClick={closeSheet}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="flex flex-col gap-0.5 border-t border-border-light pt-4">
                                    <button
                                        onClick={handleSoundToggle}
                                        className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-sm font-medium text-text-muted transition-colors hover:bg-bg-hover/50 hover:text-text-light"
                                    >
                                        {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                                        {soundEnabled ? 'Sound On' : 'Sound Off'}
                                    </button>
                                    {demoModeEnabled && (
                                        <button
                                            onClick={() => { closeSheet(); onResetCharacter() }}
                                            className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-sm font-medium text-text-muted transition-colors hover:bg-bg-hover/50 hover:text-accent-red"
                                        >
                                            <RefreshCw size={18} />
                                            Reset Character
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { closeSheet(); onLogout() }}
                                        className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-sm font-medium text-text-muted transition-colors hover:bg-bg-hover/50 hover:text-text-light"
                                    >
                                        <LogOut size={18} />
                                        Logout
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    )
}

export default MobileNav
